import { CalendarDays } from "lucide-react";

const DAY_MS = 86400000;

export const addDays = (ymd, n) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d) + n * DAY_MS);
  return dt.toISOString().slice(0, 10);
};

export const daysBetween = (startYmd, endYmd) => {
  if (!startYmd || !endYmd) return null;
  const s = Date.parse(`${startYmd}T00:00:00Z`);
  const e = Date.parse(`${endYmd}T00:00:00Z`);
  return Math.round((e - s) / DAY_MS) + 1;
};

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export function buildFestivalDetails(form, days) {
  const startLocal = new Date(`${form.startDate}T09:00`);
  const soon = new Date(Date.now() + 10 * 60000);
  const start = startLocal < soon ? soon : startLocal;
  const end = new Date(`${form.endDate}T18:00`);
  let tz = "UTC";
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch {}
  return { name: form.name.trim(), start_at: start.toISOString(), end_at: end.toISOString(), days, timezone: tz };
}

export function detailsError(form, days, availableDays) {
  if (form.name.trim().length < 2) return "Enter a festival name";
  if (!form.startDate) return "Pick a start date";
  if (form.startDate < todayYmd()) return "Start date can't be in the past";
  if (!form.endDate) return "Pick an end date";
  const span = daysBetween(form.startDate, form.endDate);
  if (span < 1) return "End date must be on or after the start date";
  if (!availableDays.includes(span)) return `Your tier is available for ${availableDays.join(" / ")} day festivals`;
  if (span !== days) return "Day count doesn't match your dates";
  return null;
}

const inputCls = "w-full mt-1 bg-transparent border border-[#262626] px-3 py-2 text-sm text-white focus:outline-none focus:border-[#CCFF00] [color-scheme:dark]";
const labelCls = "text-[10px] uppercase tracking-widest text-[#A3A3A3]";

export function FestivalDetailsStep({ form, setForm, days, availableDays, onDaysChange, error }) {
  const setStart = (v) => setForm((f) => ({ ...f, startDate: v, endDate: v ? addDays(v, days - 1) : f.endDate }));
  const setEnd = (v) => {
    setForm((f) => ({ ...f, endDate: v }));
    const span = daysBetween(form.startDate, v);
    if (span && availableDays.includes(span) && span !== days) onDaysChange(span);
  };
  const setDayCount = (n) => {
    onDaysChange(n);
    if (form.startDate) setForm((f) => ({ ...f, endDate: addDays(f.startDate, n - 1) }));
  };

  return (
    <div className="space-y-3" data-testid="festival-details-step">
      <div className="flex items-center gap-2 text-[#CCFF00]">
        <CalendarDays className="w-4 h-4" />
        <span className="text-[10px] uppercase tracking-widest">Step 2 · Festival details</span>
      </div>
      <div>
        <label className={labelCls}>Festival name</label>
        <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          maxLength={120} placeholder="e.g. Summer Squash Festival" data-testid="details-name" className={inputCls} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={labelCls}>Start date</label>
          <input type="date" min={todayYmd()} value={form.startDate} onChange={(e) => setStart(e.target.value)}
            data-testid="details-start-date" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>End date</label>
          <input type="date" min={form.startDate || todayYmd()} value={form.endDate} onChange={(e) => setEnd(e.target.value)}
            data-testid="details-end-date" className={inputCls} />
        </div>
      </div>
      <div>
        <label className={labelCls}>Day count</label>
        <div className="flex gap-2 mt-1" data-testid="details-day-count">
          {availableDays.map((n) => (
            <button key={n} type="button" onClick={() => setDayCount(n)} data-testid={`details-days-${n}`}
              className={`flex-1 px-3 py-2 text-xs uppercase tracking-wider border transition-colors ${n === days ? "border-[#CCFF00] bg-[#CCFF00] text-black font-bold" : "border-[#262626] text-[#A3A3A3] hover:border-white"}`}>
              {n} day{n === 1 ? "" : "s"}
            </button>
          ))}
        </div>
        <div className="text-[10px] text-[#525252] mt-1">Changing the day count updates your tier price.</div>
      </div>
      {error && <div className="text-[11px] text-[#FFB020]" data-testid="details-error">{error}</div>}
    </div>
  );
}
